// Universal Turbo-compatible initialization helper
// Replaces DOMContentLoaded with a solution that works with Turbo navigation

const executedCallbacks = new WeakSet()

export function turboReady(callback) {
  // Create a wrapper function that prevents duplicate execution
  const wrappedCallback = () => {
    // Skip if already executed for this callback
    if (executedCallbacks.has(callback)) return
    
    // Mark as executed
    executedCallbacks.add(callback)
    
    // Execute the callback
    callback()
  }
  
  // Handle initial page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wrappedCallback, { once: true })
  } else {
    // DOM is already loaded, execute immediately
    wrappedCallback()
  }
  
  // Handle Turbo navigation
  document.addEventListener('turbo:load', () => {
    // Clear the executed flag for this callback on new page loads
    executedCallbacks.delete(callback)
    wrappedCallback()
  })
  
  // Handle Turbo frame updates
  document.addEventListener('turbo:frame-load', () => {
    // Re-run for frame updates if needed
    wrappedCallback()
  })
}

// Alternative: One-time initialization that persists across Turbo navigations
export function turboOnce(callback) {
  const key = `turbo_once_${callback.toString().substring(0, 50)}`
  
  if (window[key]) return
  window[key] = true
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true })
  } else {
    callback()
  }
}

// Helper to run code on every Turbo navigation (including initial load)
export function onTurboLoad(callback) {
  // Run on initial load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', callback, { once: true })
  } else if (!document.documentElement.hasAttribute('data-turbo-preview')) {
    callback()
  }
  
  // Run on every Turbo navigation
  document.addEventListener('turbo:load', callback)
}

// Helper to run code before Turbo caches the page
export function beforeTurboCache(callback) {
  document.addEventListener('turbo:before-cache', callback)
}

// Export as default for convenience
export default turboReady

// Make it globally available for inline scripts
// Check if there's already a turboReady (from our safe wrapper)
if (typeof window.turboReady === 'function' && !window.turboReady.toString().includes('executedCallbacks')) {
  // There's a temporary wrapper, we need to replace it carefully
  const tempTurboReady = window.turboReady;
  window.turboReady = turboReady;
  
  // If the temp version had any queued callbacks, we don't need to worry about them
  // as they would have been executed by now or will be handled by our real implementation
  console.log('✅ Replaced temporary turboReady wrapper with real implementation');
} else {
  // No existing wrapper or it's already the real one
  window.turboReady = turboReady;
}

window.turboOnce = turboOnce
window.onTurboLoad = onTurboLoad
window.beforeTurboCache = beforeTurboCache
